import { create } from "zustand";

import { isSessionTracesEnabled } from "./session-traces-enabled";

export type DevTracePointer =
  | "chats"
  | "steps"
  | "transcript"
  | "intent"
  | "plan"
  | "hitl"
  | "checklist"
  | "composer"
  | "error";

export type DevTraceRow = {
  id: string;
  ts: string;
  chatId: string;
  event: string;
  source: "live" | "disk";
  data: unknown;
  broken: boolean;
  pointer: DevTracePointer;
};

type DevTraceState = {
  open: boolean;
  broken: boolean;
  focusPointer: DevTracePointer | null;
  rows: DevTraceRow[];
  toggle: () => void;
  setOpen: (open: boolean) => void;
  pushLive: (
    row: Omit<DevTraceRow, "id" | "source" | "broken" | "pointer"> &
      Partial<Pick<DevTraceRow, "broken" | "pointer">>,
  ) => void;
  replaceDisk: (rows: DevTraceRow[]) => void;
  setFocusPointer: (pointer: DevTracePointer | null) => void;
  clear: () => void;
};

const MAX_LIVE = 200;

export function isBrokenTraceEvent(event: string, data: unknown): boolean {
  if (event === "error" || event === "agent-stopped" || event === "build-stalled") return true;
  if (event === "tool-result" || event === "tool-output") {
    const payload =
      data && typeof data === "object"
        ? (data as { result?: { success?: boolean; status?: string; ok?: boolean } })
        : {};
    if (payload.result?.status === "waiting_for_user") return false;
    if (payload.result?.success === false || payload.result?.ok === false) return true;
    const status = String(payload.result?.status ?? "").toLowerCase();
    if (
      status === "error" ||
      status === "halted" ||
      status === "rejected" ||
      status === "validation_error" ||
      status === "needs_revision"
    ) {
      return true;
    }
  }
  if (event === "stream-ended" || event === "stream-end") {
    const payload = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
    return (
      payload.failed === true ||
      payload.stalledByWatchdog === true ||
      payload.status === "error" ||
      payload.status === "aborted"
    );
  }
  return false;
}

function toolName(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  const record = data as { tool?: unknown; payload?: { tool?: unknown } };
  if (typeof record.tool === "string") return record.tool;
  if (typeof record.payload?.tool === "string") return record.payload.tool;
  return "";
}

export type TraceLineTone = "ok" | "err" | "info";

export function traceLineTone(event: string, data: unknown): TraceLineTone {
  if (isBrokenTraceEvent(event, data)) return "err";
  if (event === "tool-result" || event === "tool-output") {
    const payload =
      data && typeof data === "object"
        ? (data as { result?: { success?: boolean; status?: string } })
        : {};
    if (payload.result?.status === "waiting_for_user") return "ok";
    if (payload.result?.success === false) return "err";
    return "ok";
  }
  if (
    event === "session-end" ||
    event === "stream-ended" ||
    event === "stream-end" ||
    event === "complete"
  ) {
    return "ok";
  }
  return "info";
}

export function pointerForTraceEvent(event: string, data: unknown): DevTracePointer {
  const tool = toolName(data).toLowerCase();
  if (event === "error" || event === "agent-stopped" || event === "build-stalled") return "error";
  if (event === "awaiting-intent-approval" || event === "intent" || tool === "submit_intent") {
    return "intent";
  }
  if (event === "awaiting-approval" || event === "plan" || tool === "submit_plan") return "plan";
  if (event === "user-question" || tool === "ask_user_question") return "hitl";
  if (event === "tool-call" || event === "tool-result" || event === "trace") return "checklist";
  if (event === "session" || event === "stream-start") return "chats";
  return "transcript";
}

export function recordDevTraceFrame(input: { chatId: string; event: string; data: unknown }) {
  if (!isSessionTracesEnabled()) return;
  const chatId = input.chatId.trim();
  if (!chatId) return;
  useDevTraceStore.getState().pushLive({
    ts: new Date().toISOString(),
    chatId,
    event: input.event,
    data: input.data,
  });
}

export const useDevTraceStore = create<DevTraceState>((set) => ({
  open: false,
  broken: false,
  focusPointer: null,
  rows: [],
  toggle: () => set((state) => ({ open: !state.open })),
  setOpen: (open) => set({ open }),
  pushLive: (row) =>
    set((state) => {
      const broken = row.broken ?? isBrokenTraceEvent(row.event, row.data);
      const next: DevTraceRow = {
        id: `live-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        source: "live",
        broken,
        pointer: row.pointer ?? pointerForTraceEvent(row.event, row.data),
        ts: row.ts,
        chatId: row.chatId,
        event: row.event,
        data: row.data,
      };
      const disk = state.rows.filter((item) => item.source === "disk");
      const live = [...state.rows.filter((item) => item.source === "live"), next].slice(-MAX_LIVE);
      return {
        rows: [...disk, ...live],
        broken: next.event === "stream-start" ? false : state.broken || broken,
      };
    }),
  replaceDisk: (rows) =>
    set((state) => ({
      rows: [...rows, ...state.rows.filter((item) => item.source === "live")],
    })),
  setFocusPointer: (focusPointer) => set({ focusPointer }),
  clear: () => set({ rows: [], broken: false, focusPointer: null }),
}));
